import type { SalesforceContact } from 'src/types/salesforce-contact';
import React from 'react';
import IconButton from '@savvy-kit/components/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { Text } from '@savvy-kit/foundations/text';
import { ExternalLink, Mail, Phone, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import TrackedButton from 'src/components/tracked/tracked-button';
import { dialpadLaunchUrl } from 'src/utils/dialpad';

import type { TrackedClickableElementName } from '@savvy/deprecated-shared/tracking/tracked-clickable-element-name';
import { textColors } from '@savvy/deprecated-shared/theme';

import type { SalesforceAccount } from '../../types/salesforce-account';
import { formatPhoneNumber } from '../../utils/formatting';
import { findCoClientContact, findPrimaryContact } from '../../utils/salesforce/account';

export const tooltipContentClassName = 'max-w-none my-px w-auto';

export default function SfContactTooltips({
  account,
  contact,
  userGraphQLData,
}: {
  readonly account: SalesforceAccount;
  readonly contact?: SalesforceContact;
  readonly userGraphQLData?: {
    readonly secondaryEmails?: readonly { readonly email: string; readonly label?: string | null }[];
  };
}) {
  return (
    <>
      <EmailTooltip account={account} contact={contact} userGraphQLData={userGraphQLData} />
      <PhoneTooltip account={account} contact={contact} />
    </>
  );
}

function SingleEmailTooltipContent({
  contact,
  userGraphQLData,
}: {
  readonly contact: SalesforceContact;
  readonly userGraphQLData?: {
    readonly secondaryEmails?: readonly { readonly email: string; readonly label?: string | null }[];
  };
}) {
  const email = contact.Email;
  const secondaryEmail = userGraphQLData?.secondaryEmails?.[0]?.email || '';
  return (
    <ul className="pr-2">
      <li className="flex flex-row gap-2 items-center">
        <div className="flex w-full items-center py-2">
          <ContactInfoListItem text={email || ''} subtitle="" />
        </div>
        <LaunchClientViaUrlIconButton
          elementName="prospects-table-row-primary-client-email-icon-with-tooltip"
          text="Gmail"
          url={prefilledRecipientGmailClientUrl(email || '')}
        />
      </li>
      {secondaryEmail && (
        <li className="flex flex-row gap-2 items-center">
          <div className="flex w-full items-center py-2">
            <ContactInfoListItem text={secondaryEmail || ''} subtitle="" />
          </div>
          <LaunchClientViaUrlIconButton
            elementName="prospects-table-row-co-client-email-icon-with-tooltip"
            text="Gmail"
            url={prefilledRecipientGmailClientUrl(secondaryEmail || '')}
          />
        </li>
      )}
    </ul>
  );
}

function SinglePhoneTooltipContent({ contact }: { readonly contact: SalesforceContact }) {
  const phone = contact.Phone;
  return (
    <ul className="pr-2">
      <li className="flex flex-row gap-2 items-center">
        <div className="flex w-full items-center py-2">
          <ContactInfoListItem text={formatPhoneNumber(phone || '')} subtitle="" />
        </div>
        <LaunchClientViaUrlIconButton
          elementName="prospects-table-row-primary-client-phone-icon-with-tooltip"
          text="Dialpad"
          url={dialpadLaunchUrl({ phoneNumber: phone || '' })}
        />
      </li>
      {contact.OtherPhone && (
        <li className="flex flex-row gap-2 items-center">
          <div className="flex w-full items-center py-2">
            <ContactInfoListItem text={formatPhoneNumber(contact.OtherPhone)} subtitle="" />
          </div>
          <LaunchClientViaUrlIconButton
            elementName="prospects-table-row-co-client-phone-icon-with-tooltip"
            text="Dialpad"
            url={dialpadLaunchUrl({ phoneNumber: contact.OtherPhone })}
          />
        </li>
      )}
    </ul>
  );
}

function EmailTooltip({
  account,
  contact,
  userGraphQLData,
}: {
  readonly account: SalesforceAccount;
  readonly contact?: SalesforceContact;
  readonly userGraphQLData?: {
    readonly secondaryEmails?: readonly { readonly email: string; readonly label?: string | null }[];
  };
}) {
  if (contact && !contact.Email) {
    return (
      <Popover>
        <PopoverTrigger
          render={
            <div tabIndex={0}>
              <IconButton
                variant="ghost"
                size="sm"
                disabled
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Mail />
              </IconButton>
            </div>
          }
        />
        <PopoverContent className={tooltipContentClassName}>
          <ul>
            <li className="flex w-full items-center px-4 py-1" key="1">
              <Text variant="body-sm" color="secondary">
                No email address added
              </Text>
            </li>
          </ul>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Popover>
      <PopoverTrigger
        render={
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Mail />
          </IconButton>
        }
      />
      <PopoverContent className={tooltipContentClassName}>
        {contact ? (
          <SingleEmailTooltipContent contact={contact} userGraphQLData={userGraphQLData} />
        ) : (
          <EmailTooltipContent account={account} />
        )}
      </PopoverContent>
    </Popover>
  );
}
function PhoneTooltip({
  account,
  contact,
}: {
  readonly account: SalesforceAccount;
  readonly contact?: SalesforceContact;
}) {
  const primaryContact = findPrimaryContact(account);
  const primaryPhone = primaryContact ? primaryContact.Phone : '';

  if (primaryPhone) {
    return (
      <Popover>
        <PopoverTrigger
          render={
            <IconButton
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Phone />
            </IconButton>
          }
        />
        <PopoverContent className={tooltipContentClassName}>
          {contact ? <SinglePhoneTooltipContent contact={contact} /> : <PhoneTooltipContent account={account} />}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        render={
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <PhoneOff className="text-secondary" />
          </IconButton>
        }
      />
      <PopoverContent className={tooltipContentClassName}>
        <ul>
          <li className="flex w-full items-center px-4 py-1" key="1">
            <Text variant="body-sm" color="secondary">
              No phone number added
            </Text>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function TooltipItem({
  text,
  subtitle,
  label,
  url,
  condense,
  elementName,
}: {
  readonly text: string;
  readonly subtitle?: string;
  readonly label: string;
  readonly url: string;
  readonly condense?: boolean;
  readonly elementName: TrackedClickableElementName;
}) {
  return (
    <li className="flex flex-row gap-0 items-center">
      <div className="flex w-full items-center py-2" key={label}>
        <ContactInfoListItem text={text} subtitle={subtitle} />
      </div>
      <LaunchClientViaUrlIconButton elementName={elementName} text={label} url={url} condense={condense} />
    </li>
  );
}

export function EmailTooltipContent({ account }: { readonly account: SalesforceAccount }) {
  const primaryContact = findPrimaryContact(account);
  const primaryEmail = primaryContact?.Email;
  const coClientContact = findCoClientContact(account);
  const coClientEmail = coClientContact?.Email;

  return (
    <ul className="pr-2">
      {primaryEmail && (
        <TooltipItem
          elementName="prospects-table-row-primary-client-email-icon-with-tooltip"
          text={primaryEmail}
          label="Gmail"
          url={prefilledRecipientGmailClientUrl(primaryEmail)}
        />
      )}
      {coClientEmail && (
        <TooltipItem
          elementName="prospects-table-row-co-client-email-icon-with-tooltip"
          text={coClientEmail}
          label="Gmail"
          url={prefilledRecipientGmailClientUrl(coClientEmail)}
        />
      )}
      {primaryEmail && coClientEmail && (
        <li className="flex flex-row gap-0 items-center">
          <div className="flex w-full items-center py-2" key="both-contacts-email">
            <div
              className="pl-4 text-sm font-normal"
              role="presentation"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Email both clients
            </div>
          </div>
          <LaunchClientViaUrlIconButton
            elementName="prospects-table-row-email-both-clients-icon-with-tooltip"
            text="Gmail"
            url={prefilledRecipientGmailClientUrl(primaryEmail, coClientEmail)}
          />
        </li>
      )}
    </ul>
  );
}

export function PhoneTooltipContent({ account }: { readonly account: SalesforceAccount }) {
  const primaryContact = findPrimaryContact(account);
  const primaryPhone = primaryContact?.Phone;
  const coClientContact = findCoClientContact(account);
  const coClientPhone = coClientContact?.Phone;

  const formatPhoneNumberHelper = (phoneNumber: string): string => {
    return formatPhoneNumber(phoneNumber.replace(/[^\d+]/g, ''));
  };

  return (
    <ul className="pr-2">
      {primaryPhone && (
        <TooltipItem
          elementName="prospects-table-row-primary-client-phone-icon-with-tooltip"
          text={formatPhoneNumberHelper(primaryPhone)}
          subtitle={primaryContact.FirstName}
          label="Dialpad"
          url={dialpadLaunchUrl({ phoneNumber: primaryPhone })}
          condense={true}
        />
      )}
      {coClientPhone && (
        <TooltipItem
          elementName="prospects-table-row-co-client-phone-icon-with-tooltip"
          text={formatPhoneNumberHelper(coClientPhone)}
          subtitle={coClientContact.FirstName}
          label="Dialpad"
          url={dialpadLaunchUrl({ phoneNumber: coClientPhone })}
          condense={true}
        />
      )}
    </ul>
  );
}

export function ContactInfoListItem({ text, subtitle }: { readonly text: string; readonly subtitle?: string }) {
  return (
    <button
      type="button"
      className="flex w-full flex-col items-start px-4 py-1 text-left hover:bg-[var(--component-hover)]"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        toast.success('Copied!');
      }}
    >
      <Text variant="body-sm" color="dark">
        {text}
      </Text>
      {subtitle && (
        <Text variant="body-sm" color="secondary">
          {subtitle}
        </Text>
      )}
    </button>
  );
}

export function LaunchClientViaUrlIconButton({
  text,
  url,
  condense,
  elementName,
}: {
  readonly text: string;
  readonly url: string;
  readonly condense?: boolean;
  readonly elementName: TrackedClickableElementName;
}) {
  return (
    <TrackedButton
      elementName={elementName}
      size="sm"
      variant="secondary"
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{ borderColor: textColors.disabled, color: textColors.charcoal }}
      className={`px-8 rounded ${condense ? 'h-[30%]' : 'h-[70%]'}`}
      render={<a href={url} target="_blank" rel="noreferrer" />}
    >
      <div className="flex flex-row gap-2">
        <Text>{text}</Text>
        <ExternalLink className="size-4" />
      </div>
    </TrackedButton>
  );
}

export const prefilledRecipientGmailClientUrl = (recipientEmailAddress: string, secondRecipientEmailAddress?: string) =>
  `https://mail.google.com/mail/u/0/?fs=1&to=${encodeURIComponent(
    recipientEmailAddress + (secondRecipientEmailAddress ? `,${secondRecipientEmailAddress}` : ''),
  )}&tf=cm`;
